import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Database, FileSpreadsheet, Plus, Trash2, GitMerge, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DatasetsList() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "company_admin" || user?.role === "platform_admin";

  const load = async () => {
    try { const { data } = await api.get("/datasets"); setDatasets(data.datasets || []); }
    catch { toast.error("Failed to load datasets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/datasets/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const autoDash = async (id) => {
    try { const { data } = await api.post(`/datasets/${id}/auto-dashboard`); toast.success("Auto-dashboard created"); nav(`/t/${slug}/dashboards/${data.id}`); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-caps mb-2">Data Layer</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900" data-testid="datasets-title">Datasets</h1>
          <p className="text-slate-500 mt-3 max-w-lg">Every file connected to your workspace. Open one to explore auto-dashboards and ask AI questions.</p>
        </div>
        {isAdmin && (
          <Link to={`/t/${slug}/upload`} data-testid="cta-upload" className="btn-primary">
            <Plus size={14} /> Upload data
          </Link>
        )}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : datasets.length === 0 ? (
        <div className="card text-center py-16">
          <Database size={28} className="mx-auto text-slate-300 mb-4" />
          <div className="text-lg mb-2 font-medium text-slate-900">No datasets yet</div>
          <div className="text-slate-500 text-sm mb-6">{isAdmin ? "Upload files or load sample data to get started." : "Your admin hasn't uploaded any data yet."}</div>
          {isAdmin && <Link to={`/t/${slug}/onboarding`} className="btn-primary">Go to onboarding</Link>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {datasets.map((d, idx) => (
            <div key={d.id} className="card fade-up flex flex-col" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`dataset-card-${d.id}`}>
              <Link to={`/t/${slug}/datasets/${d.id}`} className="block flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center">
                    <FileSpreadsheet size={16} className="text-[var(--primary)]" />
                  </div>
                  <span className="chip">{d.kind?.replace("_", " ") || d.category}</span>
                </div>
                <h3 className="font-display text-lg font-medium text-slate-900 truncate">{d.name}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{d.description || d.filename}</p>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <Metric label="Rows" value={d.row_count?.toLocaleString()} />
                  <Metric label="Cols" value={d.columns?.length} />
                  <Metric label="Quality" value={`${d.quality?.quality_score ?? "—"}`} />
                </div>
              </Link>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <button onClick={() => autoDash(d.id)} data-testid={`auto-${d.id}`} className="btn-soft flex-1 justify-center text-xs">
                  <Sparkles size={12} /> Auto-dashboard
                </button>
                {isAdmin && (
                  <button onClick={() => del(d.id, d.name)} data-testid={`delete-${d.id}`} className="btn-ghost text-xs hover:!text-[var(--danger)]">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdmin && datasets.length >= 2 && (
        <div className="mt-8 card flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center">
            <GitMerge size={16} className="text-[var(--primary)]" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-slate-900">Combine data from multiple systems</div>
            <div className="text-sm text-slate-500">Join two files by employee_id to bring payroll and engagement together.</div>
          </div>
          <Link to={`/t/${slug}/join`} className="btn-ghost">Open Join Studio</Link>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="mono text-base mt-1 text-slate-800">{value}</div>
    </div>
  );
}
